import React, { useEffect, useCallback, useState } from "react";
import { useMutation, useQuery } from "@apollo/client";
import { useSelector, useDispatch } from "react-redux";
import { setprofile } from "../../redux/user/profileSlice.js";
import EditAccountForm from "./EditAccountForm.js"; // Ensure this is exported correctly
import QUERY_USER from "../../graphql/queries/QueryUser.js";
import Breadcrumbs from "../../components/layout/Breadcrumbs.js";
import DeleteUser from "./DeleteUser.js";

import {
  Pane,
  Paragraph,
  Card,
  toaster,
  TextInputField,
  IconButton,
  Dialog,
  Button,
  EditIcon,
  TrashIcon,
  TickIcon,
  CrossIcon,
  PlusIcon
} from "evergreen-ui";

import { makeAccountInputs } from "../../util.js";
import { setpublisherGlobalData } from "../../redux/currentpublisher/publisherGlobalDataSlice.js";

import CREATE_ORDERSTATUS_MUTATION from "../../graphql/mutations/PublisherData/CreateOrderStatus.js";
import CREATE_SHIPPINGTYPE_MUTATION from "../../graphql/mutations/PublisherData/CreateShippingType.js";
import CREATE_SALESPERSON_MUTATION from "../../graphql/mutations/PublisherData/CreateSalesperson.js";
import CREATE_CUSTOMERCLASS_MUTATION from "../../graphql/mutations/PublisherData/CreateCustomerClass.js";
import CREATE_PAYMENTTERM_MUTATION from "../../graphql/mutations/PublisherData/CreatePaymentTerm.js";
import CREATE_PAYMENTTYPE_MUTATION from "../../graphql/mutations/PublisherData/CreatePaymentType.js";
import CREATE_EXPENSE_CATEGORY_MUTATION from "../../graphql/mutations/PublisherData/CreateExpenseCategory.js";

import DELETE_ORDERSTATUS_MUTATION from "../../graphql/mutations/PublisherData/DeleteOrderStatus.js";
import DELETE_SHIPPINGTYPE_MUTATION from "../../graphql/mutations/PublisherData/DeleteShippingType.js";
import DELETE_SALESPERSON_MUTATION from "../../graphql/mutations/PublisherData/DeleteSalesperson.js";
import DELETE_CUSTOMERCLASS_MUTATION from "../../graphql/mutations/PublisherData/DeleteCustomerClass.js";
import DELETE_PAYMENTTERM_MUTATION from "../../graphql/mutations/PublisherData/DeletePaymentTerm.js";
import DELETE_PAYMENTTYPE_MUTATION from "../../graphql/mutations/PublisherData/DeletePaymentType.js";
import DELETE_EXPENSE_CATEGORY_MUTATION from "../../graphql/mutations/PublisherData/DeleteExpenseCategory.js";

import UPDATE_PUBLISHER_DATA_MUTATION from "../../graphql/mutations/PublisherData/UpdatePublisherData.js";
import UPDATE_CUSTOMERCLASS_MUTATION from "../../graphql/mutations/PublisherData/UpdateCustomerClass.js";
import UPDATE_ORDERSTATUS_MUTATION from "../../graphql/mutations/PublisherData/UpdateOrderStatus.js";
import UPDATE_SHIPPINGTYPE_MUTATION from "../../graphql/mutations/PublisherData/UpdateShippingType.js";
import UPDATE_SALESPERSON_MUTATION from "../../graphql/mutations/PublisherData/UpdateSalesperson.js";
import UPDATE_PAYMENTTERM_MUTATION from "../../graphql/mutations/PublisherData/UpdatePaymentTerm.js";
import UPDATE_PAYMENTTYPE_MUTATION from "../../graphql/mutations/PublisherData/UpdatePaymentType.js";
import UPDATE_EXPENSECATEGORY_MUTATION from "../../graphql/mutations/PublisherData/UpdateExpenseCategory.js";

function UserSettings({ category, activeSection }) {
  const currentpublisher = useSelector((state) => state.currentpublisher.value);
  const profile = useSelector((state) => state.profile.value);
  const publisherGlobalData = useSelector(
    (state) => state.publisherGlobalData.value
  );
  const dispatch = useDispatch();
  const [updateCounter, setUpdateCounter] = useState(0);

  const [createOrderStatus] = useMutation(CREATE_ORDERSTATUS_MUTATION);
  const [deleteOrderStatus] = useMutation(DELETE_ORDERSTATUS_MUTATION);
  const [updatePublisherData] = useMutation(UPDATE_PUBLISHER_DATA_MUTATION);
  const [updateCustomerClass] = useMutation(UPDATE_CUSTOMERCLASS_MUTATION);
  const [updateOrderStatus] = useMutation(UPDATE_ORDERSTATUS_MUTATION);
  const [updateShippingType] = useMutation(UPDATE_SHIPPINGTYPE_MUTATION);
  const [updateSalesperson] = useMutation(UPDATE_SALESPERSON_MUTATION);
  const [updatePaymentTerm] = useMutation(UPDATE_PAYMENTTERM_MUTATION);
  const [updatePaymentType] = useMutation(UPDATE_PAYMENTTYPE_MUTATION);
  const [updateExpenseCategory] = useMutation(UPDATE_EXPENSECATEGORY_MUTATION);
  const [createShippingType] = useMutation(CREATE_SHIPPINGTYPE_MUTATION);
  const [deleteShippingType] = useMutation(DELETE_SHIPPINGTYPE_MUTATION);
  const [createSalesperson] = useMutation(CREATE_SALESPERSON_MUTATION);
  const [deleteSalesperson] = useMutation(DELETE_SALESPERSON_MUTATION);
  const [createCustomerClass] = useMutation(CREATE_CUSTOMERCLASS_MUTATION);
  const [deleteCustomerClass] = useMutation(DELETE_CUSTOMERCLASS_MUTATION);
  const [createPaymentTerm] = useMutation(CREATE_PAYMENTTERM_MUTATION);
  const [deletePaymentTerm] = useMutation(DELETE_PAYMENTTERM_MUTATION);
  const [createPaymentType] = useMutation(CREATE_PAYMENTTYPE_MUTATION);
  const [deletePaymentType] = useMutation(DELETE_PAYMENTTYPE_MUTATION);
  const [createExpenseCategory] = useMutation(CREATE_EXPENSE_CATEGORY_MUTATION);
  const [deleteExpenseCategory] = useMutation(DELETE_EXPENSE_CATEGORY_MUTATION);

  const mutations = {
    orderStatus: {
      create: createOrderStatus,
      delete: deleteOrderStatus,
      update: updateOrderStatus
    },
    shippingType: {
      create: createShippingType,
      delete: deleteShippingType,
      update: updateShippingType
    },
    salesperson: {
      create: createSalesperson,
      delete: deleteSalesperson,
      update: updateSalesperson
    },
    customerClass: {
      create: createCustomerClass,
      delete: deleteCustomerClass,
      update: updateCustomerClass
    },
    paymentTerm: {
      create: createPaymentTerm,
      delete: deletePaymentTerm,
      update: updatePaymentTerm
    },
    paymentType: {
      create: createPaymentType,
      delete: deletePaymentType,
      update: updatePaymentType
    },
    expenseCategory: {
      create: createExpenseCategory,
      delete: deleteExpenseCategory,
      update: updateExpenseCategory
    },
  };

  const globalName = {
    orderStatus: "orderstatuss",
    shippingType: "shippingtypes",
    salesperson: "salespersons",
    customerClass: "customerclasses",
    paymentTerm: "paymentterms",
    paymentType: "paymenttypes",
    expenseCategory: "expensecategorys",
  };

  const handleAddNewForeignKey = async (newValue, existingValues, fieldName) => {
    const values = [...existingValues];
    if (existingValues.map((item) => item.value).includes(newValue)) {
      toaster.warning("Value already exists!");
    } else {
      try {
        const results = await mutations[fieldName].create({
          variables: {
            publisherId: String(currentpublisher.id),
            value: newValue
          }
        });

        const resultName = "create" + fieldName.charAt(0).toUpperCase() + fieldName.slice(1);

        // Map field names to their response structure
        const responseFieldMap = {
          customerClass: 'customerClass',
          paymentTerm: 'paymentTerm',
          paymentType: 'paymentType',
          expenseCategory: 'expenseCategory',
          orderStatus: 'orderStatus',
          shippingType: 'shippingType',
          salesperson: 'salesperson'
        };

        const responseField = responseFieldMap[fieldName];
        const createdValue = {
          id: results.data[resultName][responseField].id,
          value: results.data[resultName][responseField].value,
        };

        values.push(createdValue);
        dispatch(setpublisherGlobalData({ [globalName[fieldName]]: values }));
      } catch (error) {
        toaster.danger(`Error adding new ${fieldName}: ${error.message}`);
      }
    }
  };

  const handleDeleteForeignKey = async (id, fieldName) => {
    try {
      const response = await mutations[fieldName].delete({
        variables: {
          id: id
        }
      });

      // Check if the mutation was successful
      const result = response.data[`delete${fieldName.charAt(0).toUpperCase() + fieldName.slice(1)}`];

      if (result.success) {
        // Update the local state
        const updatedValues = publisherGlobalData[globalName[fieldName]].filter(item => item.id !== id);
        dispatch(setpublisherGlobalData({ [globalName[fieldName]]: updatedValues }));

        toaster.success(`Successfully deleted ${fieldName}`);
      } else {
        // Show error message
        toaster.danger(result.errors[0] || `Error deleting ${fieldName}`);
      }
    } catch (error) {
      toaster.danger(`Error deleting ${fieldName}: ${error.message}`);
    }
  };

  const handleUpdateForeignKey = async (id, newValue, fieldName) => {
    console.log('handleUpdateForeignKey called with:', {
      id,
      newValue,
      fieldName
    });

    try {
      // Map the fieldName to the correct database field name
      const fieldNameMap = {
        orderStatus: "orderstatuss",
        shippingType: "shippingtypes",
        salesperson: "salespersons",
        customerClass: "customerclasses",
        paymentTerm: "paymentterms",
        paymentType: "paymenttypes",
        expenseCategory: "expensecategorys"
      };

      // Get the current array of values
      const currentValues = publisherGlobalData[fieldNameMap[fieldName]];
      console.log('Current values:', currentValues);

      // Update the value in the array
      const updatedValues = currentValues.map(item =>
        item.id === id ? { ...item, value: newValue } : item
      );
      console.log('Updated values:', updatedValues);

      // Map field names to their response structure
      const responseFieldMap = {
        customerClass: 'customerClass',
        paymentTerm: 'paymentTerm',
        paymentType: 'paymentType',
        expenseCategory: 'expenseCategory',
        orderStatus: 'orderStatus',
        shippingType: 'shippingType',
        salesperson: 'salesperson'
      };

      // Use direct mutation for all field types
      console.log(`Using direct Update${fieldName.charAt(0).toUpperCase() + fieldName.slice(1)} mutation with:`, {
        id,
        value: newValue
      });

      const result = await mutations[fieldName].update({
        variables: {
          id,
          value: newValue
        }
      });

      console.log(`Update${fieldName.charAt(0).toUpperCase() + fieldName.slice(1)} result:`, result);

      // Check if the mutation was successful
      const mutationName = `update${fieldName.charAt(0).toUpperCase() + fieldName.slice(1)}`;
      const responseField = responseFieldMap[fieldName];

      if (result.data?.[mutationName]?.[responseField]) {
        // Update the local state
        dispatch(setpublisherGlobalData({
          [fieldNameMap[fieldName]]: updatedValues
        }));

        // Force a re-render
        setUpdateCounter(prev => prev + 1);

        // Force a refetch of the data
        if (refetch) {
          setTimeout(() => refetch(), 100);
        }

        toaster.success(`Successfully updated ${fieldName}`);
      } else {
        throw new Error('Update failed');
      }
    } catch (error) {
      console.error(`Error updating ${fieldName}:`, error);
      toaster.danger(`Error updating ${fieldName}: ${error.message}`);
    }
  };

  const inputs = {
    orderStatus: {
      width: 200,
      comp: EditableForeignKey,
      formLabel: "Order Statuses",
      group: 1,
      handleAddNew: handleAddNewForeignKey,
      handleDelete: handleDeleteForeignKey,
      handleUpdate: handleUpdateForeignKey,
      values: publisherGlobalData?.orderstatuss || [],
    },
    shippingType: {
      width: 200,
      comp: EditableForeignKey,
      formLabel: "Shipping Types",
      group: 1,
      handleAddNew: handleAddNewForeignKey,
      handleDelete: handleDeleteForeignKey,
      handleUpdate: handleUpdateForeignKey,
      values: publisherGlobalData?.shippingtypes || [],
    },
    salesperson: {
      width: 200,
      comp: EditableForeignKey,
      formLabel: "Sales People",
      group: 1,
      handleAddNew: handleAddNewForeignKey,
      handleDelete: handleDeleteForeignKey,
      handleUpdate: handleUpdateForeignKey,
      values: publisherGlobalData?.salespersons || [],
    },
    customerClass: {
      width: 200,
      comp: EditableForeignKey,
      formLabel: "Customer Classes",
      group: 2,
      handleAddNew: handleAddNewForeignKey,
      handleDelete: handleDeleteForeignKey,
      handleUpdate: handleUpdateForeignKey,
      values: publisherGlobalData?.customerclasses || [],
    },
    paymentTerm: {
      width: 200,
      comp: EditableForeignKey,
      formLabel: "Payment Terms",
      group: 2,
      handleAddNew: handleAddNewForeignKey,
      handleDelete: handleDeleteForeignKey,
      handleUpdate: handleUpdateForeignKey,
      values: publisherGlobalData?.paymentterms || [],
    },
    paymentType: {
      width: 200,
      comp: EditableForeignKey,
      formLabel: "Royalty Payment Methods",
      group: 3,
      handleAddNew: handleAddNewForeignKey,
      handleDelete: handleDeleteForeignKey,
      handleUpdate: handleUpdateForeignKey,
      values: publisherGlobalData?.paymenttypes || [],
    },
    expenseCategory: {
      width: 200,
      comp: EditableForeignKey,
      formLabel: "Expense Categories",
      group: 3,
      handleAddNew: handleAddNewForeignKey,
      handleDelete: handleDeleteForeignKey,
      handleUpdate: handleUpdateForeignKey,
      values: publisherGlobalData?.expensecategorys || [],
    },
  };

  const userid = useSelector((state) => state.user.value);
  const {
    loading,
    error,
    data,
    refetch: _refetch,
  } = useQuery(QUERY_USER, {
    variables: { id: parseInt(userid) },
  });
  const refetch = useCallback(() => {
    setTimeout(() => _refetch(), 0);
  }, [_refetch]);

  const handleRefetchUser = () => {
    refetch();
  };

  useEffect(() => {
    if (data && data.hasOwnProperty("user")) {
      console.log("[UserSettings - QUERY_USER] Data from useQuery for user:", JSON.stringify(data.user, null, 2));
      dispatch(setprofile(data.user));
    }
  }, [dispatch, data]);

  const formInputs = makeAccountInputs(inputs);

  // Helper function to get the relevant inputs based on category and section
  const getRelevantInputs = () => {
    const allInputs = {
      orderOptions: {
        orderStatus: inputs.orderStatus,
        shippingType: inputs.shippingType,
        salesperson: inputs.salesperson
      },
      customerOptions: {
        customerClass: inputs.customerClass,
        paymentTerm: inputs.paymentTerm
      },
      royaltyExpenseOptions: {
        paymentType: inputs.paymentType,
        expenseCategory: inputs.expenseCategory
      }
    };

    if (!category || !activeSection) {
      console.warn('Missing category or activeSection:', { category, activeSection });
      return null;
    }

    return allInputs[category][activeSection];
  };

  const currentInputs = getRelevantInputs();

  // Add useEffect to log when publisherGlobalData changes
  useEffect(() => {
    console.log('publisherGlobalData updated:', publisherGlobalData);
  }, [publisherGlobalData]);

  if (loading) return null;
  if (error) return `Error! ${error.message}`;
  if (!currentInputs) return null;

  return (
    <Card
      border="default"
      padding={32}
      backgroundColor="white"
      elevation={1}
    >

      <EditableForeignKey
        {...currentInputs}
        handleAddNew={handleAddNewForeignKey}
        handleDelete={handleDeleteForeignKey}
        handleUpdate={handleUpdateForeignKey}
        fieldName={activeSection}
      />
    </Card>
  );
}

function ForeignKeyItem({ item, onDelete, onUpdate, fieldName }) {
  const [isEditing, setIsEditing] = useState(false);
  const [editValue, setEditValue] = useState(item.value);
  const [isConfirmingDelete, setIsConfirmingDelete] = useState(false);

  // Update editValue when item.value changes
  useEffect(() => {
    setEditValue(item.value);
  }, [item.value]);

  const handleUpdate = () => {
    console.log('ForeignKeyItem handleUpdate called with:', {
      id: item.id,
      editValue,
      fieldName
    });
    onUpdate(item.id, editValue, fieldName);
    setIsEditing(false);
  };

  return (
    <Pane
      display="flex"
      alignItems="center"
      marginY={8}
      padding={12}
      background="var(--palegreen)"
      borderRadius={5}
      elevation={0}
      border="1px solid var(--lightgreen)"
    >
      {isEditing ? (
        <>
          <Pane flex="1" marginRight={8} minWidth={150}>
            <TextInputField
              label=""
              value={editValue}
              onChange={e => setEditValue(e.target.value)}
              width="100%"
              height={36}
              marginBottom={0}
            />
          </Pane>
          <Pane flexShrink={0}>
            <IconButton
              icon={TickIcon}
              appearance="primary"
              intent="success"
              marginRight={8}
              onClick={handleUpdate}
            />
            <IconButton
              icon={CrossIcon}
              appearance="primary"
              intent="danger"
              onClick={() => {
                setIsEditing(false);
                setEditValue(item.value);
              }}
            />
          </Pane>
        </>
      ) : (
        <>
          <Pane flex="1">
            <Paragraph size={400} fontWeight={500} color="var(--darkgrey)">{item.value}</Paragraph>
          </Pane>
          <IconButton
            icon={EditIcon}
            appearance="minimal"
            intent="none"
            onClick={() => setIsEditing(true)}
          />
          <IconButton
            icon={TrashIcon}
            appearance="minimal"
            intent="danger"
            onClick={() => setIsConfirmingDelete(true)}
          />
        </>
      )}

      {isConfirmingDelete && (
        <Dialog
          isShown={isConfirmingDelete}
          title="Confirm Delete"
          intent="danger"
          onCloseComplete={() => setIsConfirmingDelete(false)}
          confirmLabel="Delete"
          onConfirm={() => {
            onDelete(item.id, fieldName);
            setIsConfirmingDelete(false);
          }}
        >
          Are you sure you want to delete "{item.value}"? This action cannot be undone.
        </Dialog>
      )}
    </Pane>
  );
}

function EditableForeignKey({ values = [], handleAddNew, handleDelete, handleUpdate, fieldName, formLabel, ...props }) {
  const [newValue, setNewValue] = useState("");
  const [isAdding, setIsAdding] = useState(false);
  const itemValues = Array.isArray(values) ? values : [];

  return (
    <Pane {...props}>
      <Paragraph size={500} marginBottom={16} fontWeight="bold" color="var(--darkgreen)">{formLabel}</Paragraph>

      <Card
        background="var(--offwhite)"
        padding={16}
        marginBottom={16}
        borderRadius={5}
        elevation={0}
        border="1px solid var(--lightgrey)"
      >
        {itemValues.length > 0 ? (
          itemValues.map(item => (
            <ForeignKeyItem
              key={item.id}
              item={item}
              onDelete={handleDelete}
              onUpdate={handleUpdate}
              fieldName={fieldName}
            />
          ))
        ) : (
          <Paragraph color="muted">No items added yet.</Paragraph>
        )}
      </Card>

      {isAdding ? (
        <Pane display="flex" alignItems="center" marginTop={8}>
          <TextInputField
            label=""
            value={newValue}
            onChange={e => setNewValue(e.target.value)}
            placeholder="Add new value..."
            width={250}
            marginBottom={0}
          />
          <Button
            appearance="primary"
            intent="success"
            onClick={() => {
              if (newValue.trim()) {
                handleAddNew(newValue, itemValues, fieldName);
                setNewValue("");
                setIsAdding(false);
              }
            }}
            marginLeft={8}
            marginRight={8}
          >
            Add
          </Button>
          <Button
            appearance="minimal"
            intent="danger"
            onClick={() => {
              setIsAdding(false);
              setNewValue("");
            }}
          >
            Cancel
          </Button>
        </Pane>
      ) : (
        <Button
          appearance="primary"
          intent="none"
          iconBefore={PlusIcon}
          marginTop={8}
          onClick={() => setIsAdding(true)}
          backgroundColor="var(--darkgreen)"
          color="white"
        >
          Add New {formLabel.replace(/s$/, '')}
        </Button>
      )}
    </Pane>
  );
}

export default UserSettings;
